/**
 * Schéma Zod pour le JSON officiel ATEE des calculatrices CEE.
 *
 * Source : `server/data/cee/atee-calculatrices.json`
 * Origine : ATEE — Recompilé depuis les fiches standardisées (FS) officielles.
 *
 * Le schéma est intentionnellement permissif : la structure du JSON varie
 * légèrement selon les types de formules. On valide ce qui doit l'être (codes,
 * secteurs, URLs, structure inputs) et on laisse passer le reste via passthrough
 * pour permettre au moteur de calcul d'exploiter les champs spécifiques.
 */

import { z } from 'zod';

/** Mapping secteur ATEE (texte) → code DB (BAR/BAT/IND/AGRI/TRA/RES). */
export const ATEE_SECTOR_MAP = {
  AGRICULTURE: 'AGRI',
  'BÂTIMENT_RESIDENTIEL': 'BAR',
  'BÂTIMENT_TERTIAIRE': 'BAT',
  INDUSTRIE: 'IND',
  TRANSPORT: 'TRA',
  'RÉSEAUX': 'RES',
} as const satisfies Record<string, 'BAR' | 'BAT' | 'IND' | 'AGRI' | 'TRA' | 'RES'>;

export type AteeSectorText = keyof typeof ATEE_SECTOR_MAP;
export type AteeSectorCode = (typeof ATEE_SECTOR_MAP)[AteeSectorText];

/** Libellé secteur lisible (utilisé dans `sectorLabel`). */
export const ATEE_SECTOR_LABEL: Record<AteeSectorCode, string> = {
  BAR: 'Bâtiment résidentiel',
  BAT: 'Bâtiment tertiaire',
  IND: 'Industrie',
  AGRI: 'Agriculture',
  TRA: 'Transport',
  RES: 'Réseaux',
};

/** Documents officiels d'une fiche (FS, FC, FE). */
export const ateeDocumentsSchema = z.object({
  fiche_standardisee: z.string().default(''),
  fiche_calcul: z.string().default(''),
  fiche_explicative: z.string().default(''),
});

/** Une option d'un champ select. Format hétérogène dans le JSON (value/valeur). */
export const ateeInputOptionSchema = z
  .object({
    value: z.union([z.string(), z.number()]).optional(),
    valeur: z.union([z.string(), z.number()]).optional(),
    label: z.string(),
    coeff: z.number().optional(),
    aide: z.string().optional(),
  })
  .passthrough();

/** Un champ de saisie utilisateur (input UI). */
export const ateeInputSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    type: z.enum(['number', 'select', 'radio', 'checkbox']).or(z.string()),
    requis: z.boolean().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    default: z.union([z.string(), z.number(), z.boolean()]).optional(),
    options: z.array(ateeInputOptionSchema).optional(),
    aide: z.string().optional(),
    placeholder: z.string().optional(),
  })
  .passthrough();

/** Une étape pédagogique du calcul. */
export const ateeEtapeSchema = z
  .object({
    n: z.number(),
    titre: z.string(),
    formule: z.string().optional(),
    note: z.string().optional(),
  })
  .passthrough();

/** Bloc calculatrice : tout ce qu'il faut pour simuler la prime. */
export const ateeCalculatriceSchema = z
  .object({
    /** Booléen OU objet de coefficients (selon le `formule_type`). */
    calculable: z.union([z.boolean(), z.record(z.string(), z.unknown())]),
    /** Identifiant du type de formule (ex. valeur_fixe, tableau_zones, fc_Q…). */
    formule_type: z.string(),
    unite_kwh: z.string().nullable().optional(),
    variable_id: z.string().nullable().optional(),
    variable_label: z.string().nullable().optional(),
    raison_non_calculable: z.string().nullable().optional(),
    /** Tables de coefficients (ex. {valeur_fixe: 83} ou {H1:1700,H2:1400,H3:920}). */
    donnees_lookup: z.record(z.string(), z.unknown()).nullable().optional(),
    /** Formules JavaScript-like à interpréter par le moteur de calcul. */
    formules_js: z
      .object({
        kwh_cumac_brut: z.string().nullable().optional(),
        kwh_cumac_bonifie: z.string().nullable().optional(),
        mwh_cumac: z.string().nullable().optional(),
        prime_eur: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    inputs: z.array(ateeInputSchema).nullable().optional(),
    etapes: z.array(ateeEtapeSchema).nullable().optional(),
    explication: z.string().nullable().optional(),
    COEFF_PRECARITE: z.record(z.string(), z.number()).nullable().optional(),
    COEFF_ZNI: z.record(z.string(), z.number()).nullable().optional(),
    territoire: z.string().nullable().optional(),
  })
  .passthrough();

/** Une fiche ATEE complète. */
export const ateeFicheSchema = z.object({
  code: z.string().regex(/^[A-Z]+-[A-Z]+-\d+$/i, 'Code fiche invalide'),
  titre: z.string(),
  secteur: z.string(),
  documents: ateeDocumentsSchema,
  qualification_rge_requise: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
  calculatrice: ateeCalculatriceSchema,
});

/** Métadonnées globales du JSON ATEE. */
export const ateeMetaSchema = z.object({
  source: z.string(),
  total: z.number(),
  calculables: z.number(),
  formule_prime: z.string(),
  COEFF_PRECARITE: z.record(z.string(), z.number()),
  version: z.string(),
  description: z.string(),
});

/** Le JSON ATEE complet. */
export const ateeCatalogSchema = z.object({
  meta: ateeMetaSchema,
  fiches: z.array(ateeFicheSchema),
});

export type AteeDocuments = z.infer<typeof ateeDocumentsSchema>;
export type AteeInputOption = z.infer<typeof ateeInputOptionSchema>;
export type AteeInput = z.infer<typeof ateeInputSchema>;
export type AteeEtape = z.infer<typeof ateeEtapeSchema>;
export type AteeCalculatrice = z.infer<typeof ateeCalculatriceSchema>;
export type AteeFiche = z.infer<typeof ateeFicheSchema>;
export type AteeMeta = z.infer<typeof ateeMetaSchema>;
export type AteeCatalog = z.infer<typeof ateeCatalogSchema>;

/**
 * Normalise la valeur d'une option (gère le mix `value` / `valeur` du JSON).
 */
export function ateeOptionValue(opt: AteeInputOption): string | number | undefined {
  return opt.value ?? opt.valeur;
}

/**
 * Convertit le libellé secteur ATEE en code DB.
 * Lance une erreur si le secteur est inconnu (la validation Zod devrait l'empêcher).
 */
export function mapAteeSectorToCode(secteur: string): AteeSectorCode {
  const code = (ATEE_SECTOR_MAP as Record<string, AteeSectorCode | undefined>)[secteur];
  if (!code) throw new Error(`Secteur ATEE inconnu : "${secteur}"`);
  return code;
}
